import React from 'react';
import {
  ChevronLeft, CreditCard, DollarSign, Receipt, Link2, FileText
} from 'lucide-react';

// 信息行组件
const InfoRow = ({ label, value, className = '' }) => (
  <div className={`flex justify-between items-center py-2 ${className}`}>
    <span className="text-sm text-slate-500">{label}</span>
    <span className="text-sm font-medium text-slate-800">{value || '-'}</span>
  </div>
);

// 信息卡片组件
const InfoCard = ({ title, icon: Icon, children, className = '' }) => (
  <div className={`bg-white rounded-xl border border-slate-200 overflow-hidden ${className}`}>
    <div className="flex items-center gap-2 px-4 py-3 bg-slate-50 border-b border-slate-200">
      {Icon && <Icon size={16} className="text-slate-500" />}
      <span className="text-sm font-medium text-slate-700">{title}</span>
    </div>
    <div className="p-4">
      {children}
    </div>
  </div>
);

// 付款单详情
const PaymentDetailView = ({ payment, onBack, onInvoiceClick, onSettlementClick }) => {
  if (!payment) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-slate-500">付款单不存在</p>
      </div>
    );
  }

  const isMerged = payment.isMerged;
  const delivery = payment.delivery;
  const deliveries = payment.deliveries || (delivery ? [delivery] : []);

  // 获取关联的发票单信息
  let displayInvoices = [];
  if (payment.invoiceNos?.length > 0) {
    // 从 invoiceNos 和 deliveries 中构建发票信息
    displayInvoices = payment.invoiceNos.map(invoiceNo => {
      const d = deliveries.find(del => del.invoice?.no === invoiceNo);
      if (d?.invoice) {
        return {
          docNo: invoiceNo,
          amount: d.invoice.amount,
          type: d.invoice.type,
          at: d.invoice.at,
        };
      }
      return {
        docNo: invoiceNo,
        amount: Math.round(payment.amount / payment.invoiceNos.length),
        type: '反向开票',
        at: payment.applyDate,
      };
    });
  } else if (payment.sourceInvoiceNos?.length > 0) {
    // 兼容旧字段名
    displayInvoices = payment.sourceInvoiceNos.map((no, idx) => ({
      docNo: no,
      amount: payment.sourceInvoiceAmounts?.[idx] || Math.round(payment.amount / payment.sourceInvoiceNos.length),
      type: payment.sourceInvoiceTypes?.[idx] || '反向开票',
      at: payment.sourceInvoiceDates?.[idx] || payment.applyDate,
    }));
  } else if (delivery?.invoice) {
    // 单个付款单
    displayInvoices = [{
      docNo: delivery.invoice.no,
      amount: delivery.invoice.amount,
      type: delivery.invoice.type,
      at: delivery.invoice.at,
    }];
  }

  // 获取关联的结算单信息
  let displaySettlements = [];
  if (payment.settlementNos?.length > 0) {
    // 从 settlementNos 和 deliveries 中构建结算信息
    displaySettlements = payment.settlementNos.map(settlementNo => {
      const d = deliveries.find(del => del.settlement?.settlementNo === settlementNo);
      if (d?.settlement) {
        return {
          docNo: settlementNo,
          amount: d.settlement.amount,
          actualWeight: d.settlement.actualWeight || d.storage?.actualWeight,
          unitPrice: d.settlement.unitPrice,
          confirmedAt: d.settlement.confirmedAt,
          confirmedBySupplier: d.settlement.confirmedBySupplier,
        };
      }
      return {
        docNo: settlementNo,
        amount: Math.round(payment.amount / payment.settlementNos.length),
        actualWeight: 0,
        unitPrice: 0,
        confirmedAt: payment.applyDate,
        confirmedBySupplier: true,
      };
    });
  } else if (payment.sourceSettlementNos?.length > 0) {
    // 兼容旧字段名
    displaySettlements = payment.sourceSettlementNos.map((no, idx) => ({
      docNo: no,
      amount: payment.sourceSettlementAmounts?.[idx] || Math.round(payment.amount / payment.sourceSettlementNos.length),
      actualWeight: payment.sourceSettlementWeights?.[idx] || 0,
      unitPrice: payment.sourceSettlementPrices?.[idx] || 0,
      confirmedAt: payment.applyDate,
      confirmedBySupplier: true,
    }));
  } else if (delivery?.settlement) {
    // 单个付款单
    displaySettlements = [{
      docNo: delivery.settlement.settlementNo,
      amount: delivery.settlement.amount,
      actualWeight: delivery.storage?.actualWeight,
      unitPrice: delivery.settlement.unitPrice,
      confirmedAt: delivery.settlement.confirmedAt,
      confirmedBySupplier: delivery.settlement.confirmedBySupplier,
    }];
  }

  return (
    <div className="space-y-6">
      {/* 返回按钮和标题 */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <ChevronLeft size={20} className="text-slate-600" />
        </button>
        <div className="flex-1">
          <h2 className="text-xl font-bold text-slate-800">付款单详情</h2>
          <p className="text-sm text-slate-500">付款单号: {payment.docNo}</p>
        </div>
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-green-50">
          <CreditCard size={18} className="text-green-600" />
          <span className="font-medium text-green-700">付款单</span>
        </div>
      </div>

      {/* 付款信息 */}
      <InfoCard title="付款信息" icon={CreditCard}>
        {isMerged && (
          <div className="mb-3 px-3 py-2 bg-blue-50 rounded-lg text-xs text-blue-700 flex items-center gap-2">
            <Link2 size={14} />
            <span>合并付款单 - 由 {(payment.sourceInvoiceNos?.length || 0)} 个发票单合并生成</span>
          </div>
        )}
        <div className="p-4 bg-green-50 rounded-lg mb-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-green-600">需支付金额</p>
              <p className="text-2xl font-bold text-slate-800">¥{payment.requiredAmount?.toLocaleString()}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-green-600">已支付金额</p>
              <p className={`text-2xl font-bold ${payment.paidAmount > 0 ? 'text-green-600' : 'text-slate-400'}`}>
                ¥{payment.paidAmount?.toLocaleString()}
              </p>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-green-200 flex items-center justify-between">
            <span className="text-xs text-green-600">付款状态</span>
            <span className={`text-sm font-bold ${
              payment.status === '已付款' ? 'text-green-600' :
              payment.status === '待付款' ? 'text-amber-600' : 'text-blue-600'
            }`}>
              {payment.status}
            </span>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <InfoRow label="付款单号" value={payment.docNo} />
          <InfoRow label="申请日期" value={payment.applyDate || '-'} />
          <InfoRow label="收款银行" value={payment.bankName || '-'} />
          <InfoRow label="收款账号" value={payment.bankAccount || '-'} />
          <InfoRow label="付款方式" value={payment.paymentMethod || '待确定'} />
          <InfoRow label="付款时间" value={payment.paymentTime || '待付款'} />
          <InfoRow label="交易流水号" value={payment.transactionNo || '待生成'} />
        </div>
        {/* 付款凭证 - 仅付款状态显示 */}
        {payment.status === '已付款' && payment.voucher && (
          <div className="mt-4 pt-4 border-t border-slate-200">
            <div className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-3">
              <FileText size={16} className="text-slate-400" />
              <span>付款凭证</span>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <InfoRow label="凭证号" value={payment.voucher?.no} />
              <InfoRow label="凭证类型" value={payment.voucher?.type} />
              <InfoRow label="凭证时间" value={payment.voucher?.at} />
            </div>
          </div>
        )}
      </InfoCard>

      {/* 统一的一对多渲染模式：发票单 */}
      {displayInvoices.length > 0 && (
        <InfoCard title={`关联发票单 (${displayInvoices.length})`} icon={Receipt}>
          <div className="space-y-3">
            {displayInvoices.map((invoice, index) => (
              <div key={invoice.docNo || index} className="p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <button
                    onClick={() => onInvoiceClick?.(invoice.docNo)}
                    className="text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline"
                  >
                    {invoice.docNo || '未知发票号'}
                  </button>
                  <span className="text-sm font-bold text-teal-600">¥{invoice.amount?.toLocaleString()}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs text-slate-500">
                  <span>类型: {invoice.type || '-'}</span>
                  <span>日期: {invoice.at || '-'}</span>
                </div>
              </div>
            ))}
            <div className="pt-2 border-t border-slate-200 flex justify-between text-sm">
              <span className="text-slate-500">合计金额</span>
              <span className="font-bold text-teal-600">
                ¥{displayInvoices.reduce((sum, i) => sum + (i.amount || 0), 0).toLocaleString()}
              </span>
            </div>
          </div>
        </InfoCard>
      )}

      {/* 统一的一对多渲染模式：结算单 */}
      {displaySettlements.length > 0 && (
        <InfoCard title={`关联结算单 (${displaySettlements.length})`} icon={DollarSign}>
          <div className="space-y-3">
            {displaySettlements.map((settlement, index) => (
              <div key={settlement.docNo || index} className="p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <button
                    onClick={() => onSettlementClick?.(settlement.docNo)}
                    className="text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline"
                  >
                    {settlement.docNo || '未知结算单号'}
                  </button>
                  <span className="text-sm font-bold text-orange-600">¥{settlement.amount?.toLocaleString()}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs text-slate-500">
                  <span>实重: {settlement.actualWeight || 0} 吨</span>
                  <span>单价: ¥{settlement.unitPrice || 0}/吨</span>
                  <span>状态: {settlement.confirmedBySupplier ? '已确认' : '待确认'}</span>
                  <span>确认时间: {settlement.confirmedAt || '暂无'}</span>
                </div>
              </div>
            ))}
            <div className="pt-2 border-t border-slate-200 flex justify-between text-sm">
              <span className="text-slate-500">合计金额</span>
              <span className="font-bold text-orange-600">
                ¥{displaySettlements.reduce((sum, s) => sum + (s.amount || 0), 0).toLocaleString()}
              </span>
            </div>
          </div>
        </InfoCard>
      )}

      {/* 业务说明 */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-700">
        <p className="font-medium mb-1">付款流程说明</p>
        <div className="text-green-600 space-y-1">
          <p>· 付款单由发票单发起付款申请后生成</p>
          <p>· 付款流程：待审批 - 待付款 - 已付款</p>
          <p>· 付款单和结算单、发票单支持一对多(合并)模式</p>
        </div>
      </div>
    </div>
  );
};

export default PaymentDetailView;
